/**
 * @module  support/shared/widgets
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable:no-any */ // Need to support all data types

import { Directive, OnDestroy, OnInit } from '@angular/core';
// Interfaces
import { IamResponse } from '@support/api/proxies/responses/api-999.interface';
import { StateStoreService } from '@support/state-object-hash-utilities/services/state-store.service';
import { Subject } from 'rxjs';
import { CustomerProfileService } from '@support/customer-login/services/customer-profile.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { NavbarService } from '@root/shared/services/navbar.service';
import { Router } from '@angular/router';
import { AppConfig } from '@root/app.config';
import { HttpResponse } from '@angular/common/http';
import { APP_ROUTE_CONSTANTS } from '@root/shared/constants/app-route.constants';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { SHARED_CONSTANTS } from '@root/shared/constants/shared-constants.constants';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '@support/shared/services/auth.service';

/**
 * A T-mobile custom design Auto Login Directive. Supports auto login functionality .
 */
@Directive({
  selector: '[tmoAutoLogin]',
})
export class TmoAutoLoginDirective implements OnInit, OnDestroy {
  /**
   * Subject to unsubscribe
   */
  public ngUnsubscribe: Subject<void>;
  /**
   * Stores value of API 999 response
   */
  public profile: IamResponse;
  /**
   * Stores boolean value for WaitForRoles
   */
  public waitForRoles: boolean;

  /**
   * Stores value of current host name
   */
  public redirectUri: string;
  constructor(
    private appConfig: AppConfig,
    private stateStoreService: StateStoreService,
    private customerProfileService: CustomerProfileService,
    private contentProviderService: ContentProviderService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private navbarService: NavbarService,
    private router: Router,
    private commonUtilService: CommonUtilService,
    private commonUtils: CommonUtilService,
    private authService: AuthService
  ) {
    this.ngUnsubscribe = new Subject<void>();
  }

  /**
   * Automatically called when constructor is called.
   * Used to invoke the reset function.
   * @docs-private
   * @returns void
   */
  public ngOnInit(): void {
    const win = window;
    this.redirectUri = `${win.location.protocol}//${win.location.host}`;
    let queryParam = win.location.search;
    queryParam = queryParam.substring(1);
    const params = this.commonUtils.getQueryParams(queryParam);
    if (
      this.appConfig.getConfig('enableBridging') &&
      params[SHARED_CONSTANTS.MSISDN] &&
      params[SHARED_CONSTANTS.USER_ID]
    ) {
      if (
        params[SHARED_CONSTANTS.MSISDN] &&
        params[SHARED_CONSTANTS.CODE] &&
        params[SHARED_CONSTANTS.USER_ID]
      ) {
        this.getQueryParameters(
          params[SHARED_CONSTANTS.MSISDN],
          params[SHARED_CONSTANTS.CODE],
          params[SHARED_CONSTANTS.USER_ID],
          params[SHARED_CONSTANTS.STATE]
        );
      }
    }
  }

  /**
   *  Destroys all the subscriptions
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Method to call API 999 with provided parameters from  URL
   * @param code
   * @param msisdn
   * @param userId
   * @param state
   * @return void
   */
  private getQueryParameters(msisdn: string, code: string, userId: string, state: string): void {
    this.waitForRoles = true;
    this.stateStoreUtilsService.setRedeemCardMsisdn(msisdn);
    this.customerProfileService
      .getAuthToken(code, msisdn, this.redirectUri, userId)
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (data: IamResponse) => {
          this.stateStoreUtilsService.setEservicesState(state);
          this.authService.setCookie('dnt', data.localDoNotSellSetting);
          this.stateStoreUtilsService.setCorrelation(data.correlation, true);
          // to persist interactionId
          this.stateStoreUtilsService.overrideInteractionIdForLogin(data.correlation);
          if (data.profile.hasOwnProperty('accountOwnerExists')) {
            if (!data.profile.accountOwnerExists && data.hasOwnProperty('iamToken')) {
              this.stateStoreUtilsService.setToken(data.iamToken);
              this.stateStoreUtilsService.setTempInteractionString(data.tempInteractionString);
              this.stateStoreUtilsService.setEservicesParams({
                msisdn: msisdn,
                userId: userId,
                code: code,
                state: state,
              });
              this.stateStoreUtilsService.setJWTToken(data.token);
              this.customerProfileService.popVerifyOwnershipModal('pin');
            } else {
              this.customerProfileService.popVerifyOwnershipModal('error');
            }
          } else {
            this.profile = data;
            this.setCustomerProfile(this.profile);
            this.stateStoreUtilsService.setEservicesParams({
              msisdn: msisdn,
              userId: userId,
              code: code,
              state: state,
            });
            const idTokenCookieExpires = new Date(
              new Date().getTime() +
                this.appConfig.getConfig(SHARED_CONSTANTS.PRIDA_ID_TOKEN_COOKIE_EXP_MINS) *
                  60 *
                  1000
            ).toUTCString();
            document.cookie = this.commonUtilService.messageFormatter(
              this.appConfig.getConfig('prida_tpp_id_token_set'),
              [data.token, idTokenCookieExpires]
            );
            this.stateStoreUtilsService.clearAuthCode();
            if (data && data.profile) {
              this.stateStoreUtilsService.setBscsCustomerID(data.profile.id);
            }
            this.customerProfileService
              .getPermissions(
                this.stateStoreUtilsService.getCustomerId(),
                this.stateStoreUtilsService.getAccountId()
              )
              .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
              .subscribe((permissions: HttpResponse<Object>) => {
                this.stateStoreService.setState('app.permissionObject', permissions.body);
                this.customerProfileService.permissionsLoadedSource.next(true);
              });
          }
          if (this.customerProfileService.callSetDNS(data, this.authService.getDNS())) {
            this.customerProfileService
              .setDNS(data)
              .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
              .subscribe();
          }
          const goTostate = sessionStorage.getItem(SHARED_CONSTANTS.STATE_KEY);
          if (goTostate) {
            const decodedState = decodeURI(goTostate);
            sessionStorage.removeItem(SHARED_CONSTANTS.STATE_KEY);
            if (decodedState.startsWith('/')) {
              this.router.navigateByUrl(decodedState);
            } else {
              let getEligiblePrimaryMobileNumbersPromiseResolve;
              let getCustomerDetailsPromiseResolve;
              let getBillingDetailsEventResolve;
              const getEligiblePrimaryMobileNumbersPromise = new Promise((resolve, reject) => {
                getEligiblePrimaryMobileNumbersPromiseResolve = resolve;
              });
              const getCustomerDetailsPromise = new Promise((resolve, reject) => {
                getCustomerDetailsPromiseResolve = resolve;
              });
              const getBillingDetailsEventPromise = new Promise((resolve, reject) => {
                getBillingDetailsEventResolve = resolve;
              });

              Promise.all([
                getEligiblePrimaryMobileNumbersPromise,
                getCustomerDetailsPromise,
                getBillingDetailsEventPromise,
              ]).then(() => {
                window.location.href = decodedState;
              });
              const billingDetailsEventType = 'loadAccountLanding';
              const billingDetailsEventListenerCallBack = () => {
                getBillingDetailsEventResolve();
                window.removeEventListener(
                  billingDetailsEventType,
                  billingDetailsEventListenerCallBack
                );
              };
              const billingDetailsEventListenerId = window.addEventListener(
                billingDetailsEventType,
                billingDetailsEventListenerCallBack
              );
              this.customerProfileService
                .getEligiblePrimaryMobileNumbers(msisdn, data.profile.email)
                .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
                .subscribe(() => {
                  getEligiblePrimaryMobileNumbersPromiseResolve();
                });

              this.customerProfileService
                .getCustomerDetails()
                .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
                .subscribe(() => {
                  getCustomerDetailsPromiseResolve();
                });
            }
          }
        },
        () => {
          this.contentProviderService.custProfileFailed = true;
          this.router.navigate(['/error']);
        }
      );
  }
  /**
   * Method to verify the customer Profile
   * @param custId
   * @param accountId
   * @return void
   */
  public verifyCustomerProfile(custId: String, accountId: String): void {
    this.navbarService.setIsLoggedIn(true);
    this.navbarService.navbarChangeEmitter.emit({ id: 'loggedIn' });
    this.customerProfileService.permissionsLoaded$
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: boolean) => {
        let eServicesState: string = this.stateStoreUtilsService.getEservicesState();
        const whitelistedDeeplinkUrls: string[] =
          this.appConfig.getConfig('whitelistedDeepLinkUrls') || [];
        eServicesState = eServicesState ? eServicesState : '';
        // Split at question marks but retain the question marks in result
        const [pathName, searchQuery]: string[] = eServicesState.split(/(?=\?)/g);
        if (!!pathName && whitelistedDeeplinkUrls.includes(pathName)) {
          // paramsObject converts all url params to an object
          let paramsObject = null;
          if (!!searchQuery) {
            paramsObject = this.commonUtils.getQueryParamsAsObject(searchQuery);
          }
          this.router.navigate([pathName], { queryParams: paramsObject });
          this.stateStoreUtilsService.setEservicesState('');
        } else {
          this.router.navigate([APP_ROUTE_CONSTANTS.ACCOUNT_LANDING_PATH]);
        }
      });
  }

  /**
   * Method to set the customer's profile information on state store for reusability when URL
   * has query params code,msisdn, userId
   * @param loginResponse
   * @return void
   */
  private setCustomerProfile(loginResponse: IamResponse): void {
    this.stateStoreUtilsService.setWebAuthResponse(loginResponse);
    this.stateStoreUtilsService.setMsisdn(loginResponse.profile.msisdn);
    this.customerProfileService.setMutipleAccountValue(loginResponse.profile.multipleAccounts);
    this.verifyCustomerProfile(loginResponse.profile.id, loginResponse.profile.accountId);
  }
}
